/**
 * Personal account API client — talks to `/v1/account/*`.
 *
 * Mirrors the `orgApi` style: a small factory returning typed methods,
 * `credentials: 'include'` so the session cookie travels, and a typed
 * error surfaced from the server's `{ code, message }` body.
 *
 * Unlike `orgApi` this is *not* org-scoped — every route here acts on the
 * currently signed-in user.
 */

import { UnauthenticatedError } from '../stores/types.js';

const DEFAULT_BASE = 'http://localhost:3001';

export interface AccountProfile {
  id: string;
  email: string;
  name: string | null;
  /** Whether the account has a password set (gates the "current password" field). */
  hasPassword: boolean;
}

export interface SessionRow {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  /** True for the session making this request. */
  current: boolean;
}

export class AccountApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Loose bag of server-supplied error context (e.g. blocking orgs on delete). */
  readonly details: unknown;
  constructor(args: { status: number; code: string; message: string; details?: unknown }) {
    super(args.message);
    this.name = 'AccountApiError';
    this.status = args.status;
    this.code = args.code;
    this.details = args.details;
  }
}

export interface AccountApiOptions {
  baseUrl?: string;
}

export function accountApi(opts: AccountApiOptions = {}): {
  getProfile(): Promise<AccountProfile>;
  updateProfile(patch: { name: string | null }): Promise<AccountProfile>;
  setPassword(input: { currentPassword?: string; newPassword: string }): Promise<void>;
  listSessions(): Promise<{ sessions: SessionRow[]; currentSessionId: string }>;
  revokeSession(id: string): Promise<void>;
  revokeOtherSessions(): Promise<{ revoked: number }>;
  /** Triggers a browser download of the export JSON. */
  downloadExport(): Promise<void>;
  deleteAccount(): Promise<void>;
} {
  const baseUrl = opts.baseUrl ?? import.meta.env.BLIKSYS_API_URL ?? DEFAULT_BASE;
  const root = `${baseUrl}/v1/account`;

  async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${root}${path}`, { credentials: 'include', ...init });
    if (res.status === 401) throw new UnauthenticatedError();
    if (!res.ok) {
      let body: { code?: string; message?: string; details?: unknown } = {};
      try {
        body = (await res.json()) as typeof body;
      } catch {
        /* fall through */
      }
      throw new AccountApiError({
        status: res.status,
        code: body.code ?? 'HTTP_ERROR',
        message: body.message ?? res.statusText,
        details: body.details,
      });
    }
    return (await res.json()) as T;
  }

  function jsonInit(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  return {
    async getProfile() {
      const r = await call<{ user: AccountProfile }>('/profile');
      return r.user;
    },
    async updateProfile(patch) {
      const r = await call<{ user: AccountProfile }>('/profile', jsonInit('PATCH', patch));
      return r.user;
    },
    async setPassword(input) {
      await call<{ ok: boolean }>('/password', jsonInit('POST', input));
    },
    async listSessions() {
      return call<{ sessions: SessionRow[]; currentSessionId: string }>('/sessions');
    },
    async revokeSession(id) {
      await call<{ ok: boolean }>(`/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },
    async revokeOtherSessions() {
      return call<{ ok: boolean; revoked: number }>('/sessions', { method: 'DELETE' });
    },
    async downloadExport() {
      // The export route returns a file (Content-Disposition: attachment),
      // so we fetch the blob and click a synthetic link rather than going
      // through `call` (which assumes JSON).
      const res = await fetch(`${root}/export`, { credentials: 'include' });
      if (res.status === 401) throw new UnauthenticatedError();
      if (!res.ok) {
        throw new AccountApiError({
          status: res.status,
          code: 'EXPORT_FAILED',
          message: res.statusText,
        });
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bliksys-account-export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    async deleteAccount() {
      await call<{ ok: boolean }>('/', { method: 'DELETE' });
    },
  };
}

export type AccountApi = ReturnType<typeof accountApi>;
